import { ratingOptionsConfig } from '@/constants/scorecard-configs';
import { overallEvaluationStyles } from '@/hooks/styles/overallEvaluvationStyles';
import { cloneDeep } from 'lodash-es';
import { getSelectionRatingStyle } from '@/constants/scorecard-configs';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { type InterviewType, type ScorecardType } from '@/types/interview';
import { API_SERVICE } from '@/components/shared/interviews-revamp/api';
import { useShowToast } from '@/hooks/useShowToast';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';

const overallEvaluationStyle = overallEvaluationStyles();

const containerStyles = {
  margin: '0px',
};

const labelStyles = {
  fontSize: '14px',
  fontWeight: 'Bold',
  color: 'secondary-900',
  minWidth: 'unset',
  width: '100%',
  marginRight: '0px',
  marginBottom: '12px',
};

const subLabelStyles = {
  marginBottom: '12px',
  marginRight: '0px',
};

const formControlStyles = {
  borderBottom: '1px solid',
  borderColor: 'border-color',
  pb: '2rem',
  mb: '2rem',
};

const formErrorMessageStyles = {
  mt: '1rem',
  mb: '-1rem',
};

const editorStyles = {
  enableCustomVariables: false,
  containerStyles: { width: '100%', ...containerStyles },
  subLabelStyles,
  customEditorClassName: 'scorecard-editor-class',
  editorContainerStyles: {
    border: 'none',
    height: '250px',
  },
  editorWraperStyles: {
    padding: '16px',
    border: '1px solid',
    borderColor: 'border-color',
    borderRadius: 'sm',
    overflow: 'hidden',
  },
  editorStyles: {
    padding: '0px',
    minHeight: '0px !important',
    paddingBottom: '0px !important',
  },
  toolbarWrapperStyles: { marginLeft: '-10px' },
  pluginAlignment: 'bottom',
  formControlStyles,
  formErrorMessageStyles,
};

const radioGroupStyles = {
  styles: { direction: 'column', ml: '0px', gap: '8px' },
  radioStyles: { variant: 'ats-primary-sm' },
  radioWrapperStyles: { flexDir: 'row', gap: '6px', alignItems: 'center' },
  radioLabelStyles: { size: 'sm', lineHeight: '20px', mb: '0px' },
  radioIconStyles: { width: '24px', height: '24px' },
  labelStyles: { ...labelStyles },
  subLabelStyles,
  formControlStyles,
  formErrorMessageStyles,
};

const checkboxGroupStyles = {
  styles: { direction: 'column', ml: '0px', gap: '8px' },
  checkboxStyles: { variant: 'ats-primary-sm' },
  checkboxWrapperStyles: {
    flexDir: 'row',
    gap: '6px',
    alignItems: 'center',
  },
  checkboxLabelStyles: { size: 'sm', lineHeight: '20px', mb: '0px' },
  labelStyles: { ...labelStyles },
  subLabelStyles,
  formControlStyles,
  formErrorMessageStyles,
};

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const typeMap = {
  star: {
    type: 'star',
    formControlStyles,
    labelStyles: { ...labelStyles },
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  one_to_ten: {
    type: 'rating',
    min: 1,
    max: 10,
    formControlStyles,
    labelStyles: { ...labelStyles },
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  yes_no: {
    type: 'radio2',
    options: yesNoOptions,
    formControlStyles,
    radioStyles: { variant: 'ats-primary-sm' },
    labelStyles: { ...labelStyles },
    subLabelStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  open_question: {
    type: 'editor',
    errorMessage: 'This is required field',
    ...editorStyles,
    placeholder: 'Answer here',
    hasCodeBlock: true,
    resize: true,
    pluginsConfig: ['attachment'],
    totalSizeLimitMB: 49.9,
    attachmentTooltipMessage:
      'Upload files up to a max limit of 50 MB per scorecard',
    attachmentKey: 'attachments',
    attachmentFiles: DEFAULT_ATTACHMENT_FILES.join(','),
  },
  multiple_choice: {
    type: 'radio2',
    ...radioGroupStyles,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
  checkbox: {
    type: 'checkboxGroup',
    ...checkboxGroupStyles,
    isMulti: true,
    errorMessage: 'This is required field',
    formErrorMessageStyles,
    containerStyles,
  },
};

const handleAttachmentUpload = async (
  files: File[] | any[],
  scorecard: ScorecardType,
  question: string,
  attachmentKey: string,
  attachmentType: string,
  updateInitialValues: (_updatedAttachmentObject: any) => void,
  showToast: any
) => {
  // Filter out already uploaded files
  const newFiles = files.filter((file) => !file?.id);

  if (newFiles.length === 0) return;

  const { id: scorecardId, ats_application_id: atsApplicationId } =
    scorecard || {};

  const response = await API_SERVICE.uploadAssessment({
    atsApplicationId,
    scorecardId,
    attachments: newFiles,
    question,
    attachmentType,
  });

  if (response?.attachments) {
    updateInitialValues?.({
      type: 'add',
      attachments: response.attachments,
      attachmentKey,
    });
  } else {
    console.error(
      'Attachment upload failed: No attachments in response',
      response
    );
    // Mark the failed files with hasError flag
    const failedFiles = newFiles.map((file) => {
      // For File objects, we need to assign hasError directly
      if (file instanceof File) {
        const fileWithError = file as any;
        fileWithError.hasError = true;
        return fileWithError;
      }
      // For regular objects, spread works fine
      return file;
    });

    updateInitialValues?.({
      type: 'updateError',
      attachments: failedFiles,
      attachmentKey,
    });

    showToast?.({
      title: 'File size limit exceeded',
      description:
        'The total size of all attachments should be less than 50 MB per scorecard. Please remove some files or choose smaller files.',
      status: 'error',
      isClosable: true,
    });
  }
};

const handleAttachmentDelete = async (
  scorecard: ScorecardType,
  question: string,
  attachmentId: string,
  attachmentType: string,
  attachmentKey: string,
  updateInitialValues: (_updatedAttachmentObject: any) => void,
  showToast: any
) => {
  const { id: scorecardId, ats_application_id: atsApplicationId } =
    scorecard || {};

  const response = await API_SERVICE.deleteAttachment({
    atsApplicationId,
    scorecardId,
    question,
    attachmentId,
    attachmentType,
  });

  if (response?.status === 'success') {
    updateInitialValues?.({
      type: 'remove',
      attachmentKey,
      attachmentId,
    });
  } else {
    console.error('Attachment deletion failed:', response);
    if (
      response?.errors?.[0] === 'You are not authorized to perform this action'
    ) {
      showToast?.({
        title: 'You don’t have permission to delete this attachment',
        status: 'error',
      });
    }
  }
};

export const useScorecardFormHelper = (updateInitialValues?: any) => {
  const currUser = useSelector(selectCurrUser);
  const showToast = useShowToast();
  const getScoreCardForm = ({ scorecard, isEdit }) => {
    const {
      questions = [],
      is_feedback_mandatory,
      feedback_description,
      rating_description,
      status,
      submitted_by,
    } = scorecard || {};
    const questionFields = questions?.map((item, idx) => {
      // console.log('item', item);
      const { mandatory, type, question, options, description } = item || {};

      const typeFields = typeMap?.[type];

      const baseField = {
        name: idx + 1,
        label: question,
        options: options?.map((item) => ({ value: item, label: item })),
        isRequired: mandatory || false,
        editable: isEdit,
        ...(type === 'open_question' && {
          resize: isEdit ? true : false,
        }),
        subLabel: description,
        labelStyles: { ...labelStyles },
        ...typeFields,
      };

      // Add attachment support for open_question type
      if (type === 'open_question') {
        const attachmentKey = `${idx + 1}_attachments`;
        return {
          ...baseField,
          attachmentKey: attachmentKey,
          onAttachmentChange: async (attachments) => {
            // console.log(`Question attached files:111`, attachments);
            handleAttachmentUpload(
              attachments,
              scorecard,
              item.question,
              attachmentKey,
              'question',
              updateInitialValues,
              showToast
            );
          },
          onAttachmentRemove: async (removedFile) => {
            handleAttachmentDelete(
              scorecard,
              item.question,
              removedFile.id,
              'question',
              attachmentKey,
              updateInitialValues,
              showToast
            );
          },
        };
      }

      return baseField;
    });
    const isSomeOtherUserHasDraftScorecard =
      status === 'draft' &&
      submitted_by?.id &&
      submitted_by?.id !== currUser?.id;

    const ratingOptions = cloneDeep(ratingOptionsConfig);

    // update the selected style in the rating option in case of edit
    if (
      (scorecard?.status === 'submitted' ||
        (scorecard?.status === 'draft' && !isSomeOtherUserHasDraftScorecard)) &&
      scorecard?.rating
    ) {
      ratingOptions.forEach((item) => {
        if (scorecard?.rating === item.value) {
          item['radioIconStyles'] = getSelectionRatingStyle({
            value: scorecard?.rating,
          });
          item['radioWrapperStyles'] = {
            background: item.bgColor,
            borderColor: item.selectedColor,
            color: item.selectedColor,
          };
          item['radioLabelStyles'] = {
            color: item.selectedColor,
          };
        }
      });
    }

    return [
      ...questionFields,
      {
        name: `feedback`,
        enableCustomVariables: false,
        type: 'editor',
        label: 'Feedback',
        hasCodeBlock: true,
        isRequired: is_feedback_mandatory,
        subLabel: feedback_description,
        placeholder: 'Write your feedback about the candidate here',
        editable: isEdit,
        resize: isEdit ? true : false,
        labelStyles: { ...labelStyles },
        ...editorStyles,
        formErrorMessageStyles,
        pluginsConfig: ['attachment'],
        attachmentKey: 'feedback_attachments',
        totalSizeLimitMB: 49.9,
        attachmentTooltipMessage:
          'Upload files up to a max limit of 50 MB per scorecard',
        attachmentFiles: DEFAULT_ATTACHMENT_FILES.join(','),
        onAttachmentChange: async (files) => {
          // console.log(`Feedback attached files:111`, files);
          handleAttachmentUpload(
            files,
            scorecard,
            'feedback',
            'feedback_attachments',
            'feedback',
            updateInitialValues,
            showToast
          );
        },
        onAttachmentRemove: async (removedFile) => {
          // console.log('Feedback removed file:2222', removedFile);
          handleAttachmentDelete(
            scorecard,
            null,
            removedFile.id,
            'feedback',
            'feedback_attachments',
            updateInitialValues,
            showToast
          );
        },
      },
      {
        name: 'rating',
        type: 'radio2',
        label: 'Overall evaluation',
        options: ratingOptions,
        isRequired: true,
        editable: isEdit,
        subLabel: rating_description,
        ...overallEvaluationStyle,
        containerStyles,
        formControlContainerStyles: {
          ...overallEvaluationStyle.formControlContainerStyles,
          border: 'none',
          borderRadius: '0px',
          p: '0px',
          pb: '20px',
        },
        styles: {
          flexDir: 'row',
          ml: '0px',
          gap: '6px',
        },
        radioStyles: {
          flex: '1',
          variant: 'ats-no-border',
          display: 'flex',
          marginInlineStart: '0px !important',
        },
        radioWrapperStyles: {
          className: 'overall-eval-option',
          alignItems: 'center',
          flexDir: 'row',
          gap: '6px',
          flex: 1,
          border: '1px solid',
          borderColor: 'border-color',
          borderRadius: 'sm',
          justifyContent: 'center',
          py: '6px',
        },
        onChange: (selectedItemRef) => {
          ratingOptions.forEach((item) => {
            if (item?.value === selectedItemRef) {
              item['radioIconStyles'] = {
                color: item.selectedColor,
                className: `checked_${selectedItemRef}`,
              };
              item['radioWrapperStyles'] = {
                background: item.bgColor,
                borderColor: item.selectedColor,
                color: item.selectedColor,
              };
              item['radioLabelStyles'] = {
                color: item.selectedColor,
              };
            } else {
              item['radioIconStyles'] = {
                className: 'primary-900',
              };
              item['radioWrapperStyles'] = {
                background: 'white',
                borderColor: 'border-color',
                color: 'primary-900',
              };
              item['radioLabelStyles'] = {
                color: 'primary-900',
              };
            }
          });
        },
        formErrorMessageStyles,
      },
    ];
  };

  const getInitialValues = ({ isSubmitted, scorecard }) => {
    if (isSubmitted) {
      const {
        rating,
        feedback,
        questions,
        feedback_description,
        rating_description,
        attachments = [],
        feedback_attachments = [],
      } = scorecard;
      const questionAnswers = questions?.reduce((acc, curr, idx) => {
        const { answer, attachments: questionAttachments = [] } = curr || {};
        return {
          ...acc,
          [idx + 1]: answer || '',
          [`${idx + 1}_attachments`]: questionAttachments || [],
        };
      }, {});
      return {
        rating,
        feedback,
        feedback_description,
        rating_description,
        attachments: attachments || [],
        feedback_attachments: feedback_attachments || [],
        ...questionAnswers,
      };
    }
    return {};
  };

  return {
    getInitialValues,
    getScoreCardForm,
  };
};

export const useScorecardHelper = ({
  interview,
  scorecards,
}: {
  interview: InterviewType;
  scorecards: ScorecardType[];
}) => {
  const currUser = useSelector(selectCurrUser);
  const canEditOthersScorecard = interview.can_edit;
  const isOneOnOneInterview =
    interview.kind === 'one_on_one' || interview.participants.length === 1;
  // Normalize to array: API may return object (e.g. { current_interview: [...] }) from transformResponse
  const scorecardsList = Array.isArray(scorecards) ? scorecards : [];
  const isCurrUserHasScorecard = scorecardsList.some(
    (scorecard) => scorecard?.reviewer?.id === currUser?.id
  );

  const prioritizedScorecards = [...scorecardsList].sort((a, b) => {
    if (a.reviewer.id === currUser?.id) return -1;
    if (b.reviewer.id === currUser?.id) return 1;
    return 0;
  });
  const currentScorecard = scorecardsList.find(
    (scorecard) => scorecard?.reviewer?.id === currUser?.id
  );

  const isSomeOtherUserHasDraftScorecardForCurrentScorecard =
    currentScorecard?.status === 'draft' &&
    currentScorecard?.submitted_by?.id &&
    currentScorecard?.submitted_by?.id !== currUser?.id;

  return {
    canEditOthersScorecard,
    isOneOnOneInterview,
    isCurrUserHasScorecard,
    scorecards: prioritizedScorecards,
    currentScorecard,
    isSomeOtherUserHasDraftScorecardForCurrentScorecard,
  };
};
